import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

import {
	Users,
	UserCheck,
	MessageSquare,
	FileText
} from "lucide-react";
import AdminHeader from "./Header/AdminHeader";
import AdminFooter from "./Footer/AdminFooter";
interface AdminLayoutProps {
	children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
	const { signOut } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();

	const navigation = [
		{ name: "Users", href: "/admin-users", icon: Users },
		{ name: "Messages", href: "/admin-messaging", icon: MessageSquare },
		{ name: "FAQs", href: "/admin-faq", icon: FileText },
	];

	const handleSignOut = async () => {
		try {
			await signOut();
			navigate("/login");
		} catch (error) {
			console.error("Error signing out:", error);
		}
	};

	return (
		<div className="min-h-screen bg-sage-50">
			<AdminHeader
				onHandleSignOut={handleSignOut}
				navigation={navigation}
			/>

			{/* Bottom Navigation (Mobile) */}
			<div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-sage-200 z-40">
				<div className="grid grid-cols-5 gap-1 px-2 py-2">
					{navigation.map((item) => {
						const isActive = location.pathname === item.href;
						return (
							<Link
								key={item.name}
								to={item.href}
								className={cn(
									"flex flex-col items-center justify-center py-2 px-1 rounded-lg text-xs font-medium transition-colors",
									isActive
										? "bg-sage-100 text-sage-900"
										: "text-gray-600 hover:text-sage-900 hover:bg-sage-50",
								)}
							>
								<item.icon className="w-5 h-5 mb-1" />
								<span className="truncate">{item.name}</span>
							</Link>
						);
					})}
					{/* Applications Button in Mobile Bottom Nav */}
					<Link
						to="/admin-applications"
						className={cn(
							"flex flex-col items-center justify-center py-2 px-1 rounded-lg text-xs font-medium transition-colors",
							location.pathname === "/admin-applications"
								? "bg-primary-500 text-white"
								: "bg-primary-100 text-primary-800 hover:bg-primary-200",
						)}
					>
						<UserCheck className="w-5 h-5 mb-1" />
						<span className="truncate">Apps</span>
					</Link>
				</div>
			</div>

			{/* Main Content */}
			<main className="pt-16 pb-20 md:pb-8">
				<div className="container py-8">
					{children}
				</div>
			</main>
			<AdminFooter onHandleSignOut={handleSignOut} />
		</div>
	);
};

export default AdminLayout;
